import React, { useContext, useEffect, useState } from "react"
import { Link } from "react-router-dom"
import { modalContext } from "../providers/modal.provider"
import { hasRememberedSession, profileContext } from "../providers/profile.provider"
import { useTheme } from "../providers/theme.provider"
import { APP_VERSION } from "../settings/version"
import PallinoGuide from "../landing/PallinoGuide"
import { ClosingSky, HeroScene, Notebook } from "../landing/Scenes"
import DemoAgenda from "../landing/demos/DemoAgenda"
import DemoSub from "../landing/demos/DemoSub"
import DemoMonth from "../landing/demos/DemoMonth"
import DemoSchema from "../landing/demos/DemoSchema"
import { DemoField, DemoLog, DemoRoles, DemoSheet } from "../landing/demos/Snippets"
import { readLandingChoice, saveLandingChoice } from "../landing/landingChoice"
import {
	CLOSING,
	CONTACT_EMAIL,
	FAQ,
	GUIDE_STOPS,
	PRICES,
	RECOGNIZE,
	ROLES,
	SECTIONS,
	SPORTS,
} from "../landing/content"
import "./landing.css"

// Il sito: guidato, illustrato, e con i pezzi d'app che si toccano
// (docs/sito.md). Chi arriva sceglie gli sport che insegna e se è un
// maestro o un circolo, e la pagina sotto cambia: le sezioni, gli esempi, i
// piani, i bottoni. I testi stanno in landing/content.js.

const DEMOS = {
	agenda: DemoAgenda,
	sub: DemoSub,
	month: DemoMonth,
	schema: DemoSchema,
	sheet: DemoSheet,
	roles: DemoRoles,
	log: DemoLog,
	field: DemoField,
}

const MAIL = `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent("inCampo, vorrei saperne di più")}`

const listText = words =>
	words.length === 1 ? words[0] : `${words.slice(0, -1).join(", ")} e ${words[words.length - 1]}`

const Landing = () => {
	const Modal = useContext(modalContext)
	const openLogin = () => Modal.open("auth")
	const openSignup = () => Modal.open("auth", "signup")

	// Il sito non entra da solo nell'app: a chi è già dentro propone di
	// aprirla. Finché Firebase non ha risposto ci si fida di quello che il
	// telefono ricorda dell'ultimo accesso, così il bottone giusto c'è da
	// subito; poi vale la risposta di Firebase.
	const { loading, coach } = useContext(profileContext)
	const isSignedIn = Boolean(coach) || (loading && hasRememberedSession())

	// Il lettering ha "Campo" in petrolio: sul fondo scuro serve la versione
	// chiara, altrimenti del marchio si legge solo "in".
	const { theme } = useTheme()
	const logoSuffix = theme === "dark" ? "_dark" : ""

	// Le due scelte che guidano la pagina, ricordate per la registrazione
	const [sports, setSports] = useState(() => {
		const saved = readLandingChoice()?.sports.filter(id => SPORTS.some(s => s.id === id))
		return saved?.length ? saved : ["tennis"]
	})
	const [role, setRole] = useState(() => readLandingChoice()?.role ?? "maestro")
	useEffect(() => saveLandingChoice({ sports, role }), [sports, role])

	const toggleSport = id => {
		const next = sports.includes(id) ? sports.filter(s => s !== id) : [...sports, id]
		// almeno uno: senza sport non c'è niente da raccontare
		if (next.length === 0) return
		setSports(SPORTS.map(s => s.id).filter(s => next.includes(s)))
	}

	const [lead, ...others] = sports
	const sportNames = SPORTS.filter(s => sports.includes(s.id)).map(s => s.name)
	const isCoach = role === "maestro"
	const prices = PRICES[role]

	// `onBefore`: chi lo mette in un foglio lo chiude prima di aprire altro
	const MainCta = ({ className = "", onBefore }) =>
		isSignedIn ? (
			<Link className={`btn btn-accent ${className}`} to="/lessons" onClick={onBefore}>
				Apri le tue lezioni
			</Link>
		) : isCoach ? (
			<button
				type="button"
				className={`btn btn-accent ${className}`}
				onClick={() => {
					onBefore?.()
					openSignup()
				}}>
				Inizia gratis
			</button>
		) : (
			<a className={`btn btn-accent ${className}`} href={MAIL} onClick={onBefore}>
				Parliamone
			</a>
		)
	const SecondCta = () =>
		isSignedIn ? null : isCoach ? (
			<a className="btn btn-quiet" href={MAIL}>
				Oppure scrivimi
			</a>
		) : (
			<button type="button" className="btn btn-quiet" onClick={openSignup}>
				Oppure inizia gratis
			</button>
		)

	return (
		<div className="landing">
			<header className="landing-header">
				<img src={require(`../images/incampo_logo${logoSuffix}.png`)} alt="inCampo" />
				{isSignedIn ? (
					<Link className="btn btn-fill" to="/lessons">
						Apri l'app
					</Link>
				) : (
					<button type="button" className="btn btn-fill" onClick={openLogin}>
						Accedi
					</button>
				)}
			</header>

			<section className="hero" id="apertura">
				<HeroScene others={others} />
				<div className="hero-copy">
					<h1>
						Tu insegni. <em>inCampo segna.</em>
					</h1>
					<p>
						Il gestionale di chi insegna {listText(sportNames.map(n => n.toLowerCase()))}.
						Segni la lezione mentre esci dal campo: presenze, recuperi, pagamenti e
						calendario si sistemano da soli, e a fine mese i conti sono già fatti.
					</p>

					<div className="chooser card" id="scelta">
						<h2>Cosa insegni?</h2>
						<p className="muted">Anche più di uno. Non siamo gelosi.</p>
						<div className="chooser-sports">
							{SPORTS.map(sport => (
								<button
									type="button"
									key={sport.id}
									aria-pressed={sports.includes(sport.id)}
									className={`sport-chip${sports.includes(sport.id) ? " is-on" : ""}`}
									onClick={() => toggleSport(sport.id)}>
									<span className={`sport-dot is-${sport.id}`} />
									{sport.name}
								</button>
							))}
						</div>
						<h2 className="chooser-second">E tu chi sei?</h2>
						<div className="chooser-roles">
							{ROLES.map(option => (
								<button
									type="button"
									key={option.id}
									aria-pressed={role === option.id}
									className={`role-card${role === option.id ? " is-on" : ""}`}
									onClick={() => setRole(option.id)}>
									<b>{option.title}</b>
									<span>{option.text}</span>
								</button>
							))}
						</div>
					</div>

					<div className="cta-row">
						<MainCta />
						<SecondCta />
					</div>
				</div>
			</section>

			<section className="recognize">
				<Notebook />
				<p className="kicker">Ti riconosci?</p>
				<h2>{RECOGNIZE[role].title}</h2>
				<ul>
					{RECOGNIZE[role].items.map(item => (
						<li key={item}>{item}</li>
					))}
				</ul>
				<p className="recognize-end">Ecco. Adesso basta.</p>
			</section>

			{SECTIONS[role].map((section, index) => {
				const Demo = DEMOS[section.demo]
				const flip = index % 2 === 1
				return (
					<section key={section.id} id={section.id} className={`feature${flip ? " is-flipped" : ""}`}>
						<div className="feature-text">
							<div className="says-slot" data-guide-anchor />
							<p className="kicker">{section.kicker}</p>
							<h2>{section.title}</h2>
							<p className="muted">{section.text}</p>
						</div>
						{Demo && (
							<div className="feature-demo">
								<Demo />
							</div>
						)}
					</section>
				)
			})}

			<section className="prices" id="prezzi">
				<div className="says-slot" data-guide-anchor />
				<p className="kicker">Quanto costa</p>
				<h2>{prices.title}</h2>
				<p className="muted">{prices.lead}</p>
				<div className="plans">
					{prices.plans.map(plan => (
						<div key={plan.name} className={`plan card${plan.pick ? " is-pick" : ""}`}>
							{plan.pick && <span className="plan-pick">Consigliato</span>}
							<div className="plan-head">
								<div>
									<h3>{plan.name}</h3>
									<p className="muted">{plan.who}</p>
								</div>
								<div className="plan-price">
									<b>{plan.amount}</b>
									{plan.per && <small> {plan.per}</small>}
									<span>{plan.year}</span>
								</div>
							</div>
							<div className="plan-limits">
								{plan.limits.map(([value, label]) => (
									<span key={label}>
										<b>{value}</b>
										<small>{label}</small>
									</span>
								))}
							</div>
							{plan.plus && <p className="plan-plus">{plan.plus}</p>}
							<ul>
								{plan.features.map(feature => (
									<li key={feature}>{feature}</li>
								))}
							</ul>
						</div>
					))}
				</div>
				<div className="plan-notes">
					{prices.notes.map(note => (
						<div key={note.title}>
							<b>{note.title}</b>
							<p className="muted">{note.text}</p>
						</div>
					))}
				</div>
				<p className="muted small">Prezzi di lancio: possono ancora cambiare.</p>
			</section>

			<section className="about card" id="chi-sono">
				<div className="about-head">
					<div className="about-photo" data-guide-anchor>
						<img src={require("../images/luca.jpg")} alt="Luca Morosini" width="92" height="92" />
					</div>
					<div>
						<p className="kicker">Chi sono</p>
						<h2>Ciao, sono Luca.</h2>
					</div>
				</div>
				<p className="muted">
					Ho studiato informatica all'università, poi ho scambiato la tastiera con la
					racchetta. La tastiera non l'ha presa bene.
				</p>
				<p className="muted">
					Con tre compagni di università ho fondato{" "}
					<a href="https://www.instagram.com/squiseat_/" target="_blank" rel="noopener noreferrer">
						Squiseat
					</a>
					, un'app bolognese contro lo spreco di cibo: i ristoranti vendevano a metà prezzo
					quello che a fine giornata sarebbe finito nel bidone. Lì ho imparato a fare app che
					la gente usa davvero.
				</p>
				<p className="muted">
					Insegno tennis da quattro anni: ho cominciato al Centro Sportivo Biavati, oggi sono
					al Siro e al Country Club Castel Maggiore. Istruttore di secondo grado, classifica
					3.1 (quando il rovescio collabora).
				</p>
				<p className="muted">
					inCampo è dove le due cose si sono incontrate: il maestro aveva un quaderno con più
					frecce di una mappa del tesoro, l'informatico sapeva come sistemarlo. L'ho scritto
					per me, poi l'hanno chiesto i colleghi.
				</p>
			</section>

			<section className="faq" id="domande">
				<div className="says-slot" data-guide-anchor />
				<p className="kicker">Domande frequenti</p>
				<h2>Le cose che ci chiedono tutti</h2>
				<div className="faq-list">
					{FAQ[role].map(item => (
						<details key={item.q} className="card">
							<summary>
								{item.q}
								<span className="faq-plus" aria-hidden="true">
									+
								</span>
							</summary>
							<p className="muted">{item.a}</p>
						</details>
					))}
				</div>
			</section>

			<ClosingSky>
				<h2>{CLOSING[role]}</h2>
				<div className="cta-row is-centered">
					<MainCta />
					<SecondCta />
				</div>
			</ClosingSky>

			<PallinoGuide
				stops={[
					...SECTIONS[role],
					...GUIDE_STOPS.map(stop => (stop.id === "prezzi" ? { ...stop, say: prices.say } : stop)),
				]}
				lead={lead}
				cta={close => <MainCta className="guide-cta" onBefore={close} />}
				home="pallino-casa"
				end="pallino-fine"
				night={theme === "dark"}
			/>

			<footer className="landing-footer">
				<span>© {new Date().getFullYear()} inCampo · Luca Morosini</span>
				<span className="landing-footer-links">
					<a href={`mailto:${CONTACT_EMAIL}`}>Scrivimi</a>
					<a href="/privacy.html" target="_blank" rel="noopener noreferrer">
						Privacy
					</a>
					<a href="/termini.html" target="_blank" rel="noopener noreferrer">
						Termini
					</a>
					<span>v{APP_VERSION}</span>
				</span>
			</footer>
		</div>
	)
}

export default Landing
